import { useCallback, useEffect, useMemo, useState } from "react";
import { IGif, IGifList } from "@/common/services/types";
import { useFeedsStore } from "@/common/store/feedsStore";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { useNichesStore } from "@/common/store/storeNiches";
import { AxiosError } from "axios";
import urlPrefetcher from "@/common/utils/urlPrefetcher";
import { useBoostedGifStore } from "@/common/store/boostedGifStore";

interface IOptions {
  canRunRequest?: boolean;
}

export const usePaginatedFeed = (
  keyParams: (string | number | boolean | undefined)[],
  func: (page?: number) => Promise<IGifList | undefined>,
  { canRunRequest }: IOptions = { canRunRequest: true },
) => {
  const key = keyParams.join(":");

  const feed = useFeedsStore((state) => state.getFeed(key));
  const appendFeed = useFeedsStore((state) => state.appendFeed);
  const updateFeed = useFeedsStore((state) => state.updateFeed);
  const updateGifs = useGifStore((state) => state.updateGifs);
  const getGifById = useGifStore((state) => state.getGifById);
  const gifsData = useGifStore((state) => state.gifs);
  const updateBoostedGifs = useBoostedGifStore((state) => state.updateGifs);
  const getBoostedGifById = useBoostedGifStore((state) => state.getGifById);
  const boostedGifsData = useBoostedGifStore((state) => state.gifs);
  const updateUsers = useUsersStore((state) => state.updateUsers);
  const updateNiches = useNichesStore((state) => state.updateNiches);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const { ids = [], boostedIds = [], page, pages, total, feedId } = feed || {};

  const fetchRequest = useCallback(async (page?: number) => {
    try {
      setError(null);
      setLoading(true);

      const res = await func(page);

      if (res) {
        updateGifs(res.gifs);
        updateUsers(res.users);
        updateNiches(res.niches);

        res.boosted_gifs && updateBoostedGifs(res.boosted_gifs);

        appendFeed(key, {
          page: res.page,
          pages: res.pages,
          total: res.total,
          feedId: res.feed_id,
          ids: res.gifs.map(gif => gif.id),
          boostedIds: res.boosted_gifs?.map(gif => gif.id),
        });

        urlPrefetcher.run(res.prefetch ?? []);
      }
    } catch (e) {
      const error = e as AxiosError;
      setError(error);
      console.error("Error reading gifs.", error);
    } finally {
      setLoading(false);
    }
  }, [func]);

  const update = useCallback((gifs: IGif[]) => {
    updateFeed(key, {
      ids: gifs.map(gif => gif.id),
    });
  }, [feed]);

  const hasMore = useMemo(() => page && pages ? page < pages : false, [page, pages]);

  const loadMore = useCallback(async () => {
    if (hasMore && page) {
      await fetchRequest(page + 1);
    }
  }, [page, hasMore, fetchRequest]);

  useEffect(() => {
    if (feed === undefined && canRunRequest) {
      fetchRequest();
    }
  }, [feed, fetchRequest, canRunRequest]);

  const gifs = useMemo(() => {
    const gifs: IGif[] = [];

    ids.forEach(id => {
      if (boostedIds.includes(id)) {
        return;
      }

      const gif = getGifById(id);
      !!gif && gifs.push(gif);
    });

    return gifs;
  }, [ids, getGifById, gifsData, boostedIds]);

  const boostedGifs = useMemo(() => {
    const gifs: IGif[] = [];

    boostedIds.forEach(id => {
      const gif = getBoostedGifById(id);
      !!gif && gifs.push(gif);
    });

    return gifs;
  }, [boostedIds, getBoostedGifById, boostedGifsData]);

  const isInitialLoading = feed === undefined && canRunRequest && !error;
  const isLoading = loading || isInitialLoading;

  return {
    gifs,
    boostedGifs,
    total: total || null,
    feedId,
    loading: !!isLoading,
    error,
    hasMore,
    loadMore,
    update,
  };
};
